import { FieldInputProps, FieldMetaProps } from 'formik';
import { memoize } from 'lodash-es';
import React from 'react';

import * as Interfaces from './interfaces';
import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Field information
   */
  field: FieldInputProps<string>;
  /**
   * metadata information
   */
  meta: FieldMetaProps<string>;
  /**
   * Optional render mode for the date either in full (dd/mm/yyyy) or partial (mm/yyyy). Defaults to "FullDate"
   */
  mode?: Interfaces.Mode;
  /**
   * Optional divider char between each date section (defaults to '/')
   */
  divider?: string;
  /**
   * Optional overriding accessibility label for screen-reader users. Defaults to 'date'
   */
  ariaLabel?: string;
  /**
   * (Optional) Disabled the date input from been editable
   */
  disabled: boolean;
  /**
   * Optional character to cover unfilled parts of the mask. Defaults to unfilled parts will be empty string.
   */
  maskChar?: string;
};

const getConfig = memoize(
  (divider: string, mode: Interfaces.Mode) => {
    switch (mode) {
      case 'FullDate':
        return {
          placeholder: `DD ${divider} MM ${divider} YYYY`,
          mask: `99 ${divider} 99 ${divider} 9999`
        };
      case 'MonthYear':
        return {
          placeholder: `MM ${divider} YYYY`,
          mask: `99 ${divider} 9999`
        };
      case 'MonthYearShort':
        return {
          placeholder: `MM ${divider} YY`,
          mask: `99 ${divider} 99`
        };
    }
  },
  (divider, mode) => `${divider}|${mode}`
);

const Input: React.FC<ComponentProps> = ({
  maskChar = null,
  mode = 'FullDate',
  divider = '/',
  field,
  meta,
  ariaLabel = 'date',
  disabled
}) => {
  const isError = meta.touched && meta.error ? true : false;

  // Add empty ' ' char between the different date parts
  const fieldValue = field.value.replace('/', ` ${divider} `);
  const { placeholder, mask } = getConfig(divider, mode);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  return (
    <Styled.StyledInput
      {...field}
      isError={isError}
      mode={mode}
      value={fieldValue}
      placeholder={placeholder}
      aria-label={ariaLabel}
      mask={mask}
      disabled={disabled}
      maskChar={maskChar}
      aria-invalid={!!isError}
      aria-labelledby={`${field.name}.label`}
      autoComplete='off'>
      {({ isError, ...rest }) => <input disabled={disabled} type='tel' {...rest} />}
    </Styled.StyledInput>
  );
};

export default React.memo(Input);
